import { useRhUserAtomValue } from "@RHCommerceDev/hooks/atoms";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import { countries } from "@RHCommerceDev/resources/countries-config.json";
import { isGDPRCompliantORCookieAccepted } from "@RHCommerceDev/utils/analytics/utils";
import { COOKIE_WITH_SESSION } from "@RHCommerceDev/utils/constants";
import getCountryFromUrl from "@RHCommerceDev/utils/getCountryFromUrl";
import { useCallback, useEffect, useState } from "react";
import yn from "yn";
import { EmailPopUp } from "./types";

// Create a session-level variable to track if popup has been shown
// This is outside the component to persist across renders and country changes
const GLOBAL_SESSION_KEY = "RH_EMAIL_POPUP_SHOWN_IN_SESSION";

function useCheckEmailPopUp(): EmailPopUp {
  const env = useEnv();
  const country = getCountryFromUrl();
  const { userType } = useRhUserAtomValue();
  const [isPopClosed, setIsPopClosed] = useState(false);
  const [emailPopupData, setEmailPopupData] = useState<any>(null);

  // Initialize state on mount
  useEffect(() => {
    if (processEnvServer) return;

    // Check if popup has been shown this session
    const popupShownInSession =
      sessionStorage.getItem(GLOBAL_SESSION_KEY) === "true";

    // Get current popup data
    try {
      const sessionData = sessionStorage.getItem(COOKIE_WITH_SESSION);
      const parsedData = sessionData ? JSON.parse(sessionData) : null;
      setEmailPopupData(parsedData);

      // If popup was closed or shown already in this session, keep it closed
      if (popupShownInSession || (parsedData && parsedData.isPopClosed)) {
        setIsPopClosed(true);
      }
    } catch (error) {
      console.error("Error initializing email popup state:", error);
    }
  }, []);

  // Determine if the popup should be rendered based on user type and country
  const renderEmailSignup =
    !["contract", "trade"].includes(userType?.toLowerCase() || "") &&
    ((yn(env?.FEATURE_EMAIL_MARKETING_POPUP_NA) &&
      !countries[country].emailPopup.value) ||
      countries[country].emailPopup.value);

  // Method to set cookie with proper session tracking
  const setSessionCookie = useCallback(
    (key: string, value: any) => {
      if (!isGDPRCompliantORCookieAccepted()) return;

      try {
        // Update local state
        if (key === COOKIE_WITH_SESSION) {
          setEmailPopupData(value);

          // If we're closing the popup, mark it as shown for this session
          if (value.isPopClosed) {
            setIsPopClosed(true);
            sessionStorage.setItem(GLOBAL_SESSION_KEY, "true");
          }
        }

        // Save to session storage
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error("Error setting session cookie:", error);
      }
    },
    [isGDPRCompliantORCookieAccepted]
  );

  // Handle closing the popup
  const resetCookieClicked = useCallback(() => {
    try {
      const currentData = emailPopupData || {};
      const updatedData = {
        ...currentData,
        clickCount: (currentData.clickCount || 0) + 1,
        isPopClosed: true
      };

      setSessionCookie(COOKIE_WITH_SESSION, updatedData);

      // Mark that popup has been shown in this session
      sessionStorage.setItem(GLOBAL_SESSION_KEY, "true");
    } catch (error) {
      console.error("Error in resetCookieClicked:", error);
    }
  }, [emailPopupData, setSessionCookie]);

  // Determine if popup should be closed based on various conditions
  const isClosed =
    isPopClosed ||
    (!processEnvServer &&
      sessionStorage.getItem(GLOBAL_SESSION_KEY) === "true") ||
    !renderEmailSignup;

  return {
    renderEmailSignup,
    isClosed,
    setSessionCookie,
    emailPopupData,
    resetCookieClicked
  };
}

export default useCheckEmailPopUp;
